import React, { useState, useEffect, useCallback } from "react";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { useLoading } from "../../../../context/LoadingContext";
import DataTable from "../../../Requirement/components/common/ReusableTable";
import SearchFilter from "../../../Requirement/components/common/SearchFilter";
import dayjs from "dayjs";
import {
  Tooltip,
  IconButton,
  Select,
  MenuItem,
  Switch,
  Button,
  FormControlLabel,
} from '@mui/material';

import {
  fetchData,
  searchfetchQueries,
} from "../../../Requirement/services/RequirementServices";
import AlertModal from "../../../Requirement/components/common/AlerModel";
import RequirementForm from "./RequirementForm";
import MatchingOffers from "./component/MatchingOffers";
import { styled } from '@mui/material/styles';
import RequirementDetails from "./component/RequirementDetails";
const Requirements = () => {
  const navigate = useNavigate();
  const { setLoading } = useLoading();
  const [activeUser, setActiveUser] = useState("");
  const [buyerInfo, setBuyerInfo] = useState([]);
  const [mode, setMode] = useState("Open");
  const [requirementList, setRequirementList] = useState([]);
  const [rowStart, setRowStart] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [totalItems, setTotalItems] = useState(0);
  const [searchValues, setSearchValues] = useState({
    requirementNumber: "",
    searchProduct: "",
    searchProductGrade: "",
    searchQuantityRange: "",
    offerStatus: "",
  });
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [formMode, setFormMode] = useState(null);
  const [productList, setProductList] = useState([]);
  const [productGradeList, setProductGradeList] = useState([]);
  const [allProductMaster, setAllProductMaster] = useState([]);
  const [matchingOffers, setMatchingOffers] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [matchingOffersRequirement, setMatchingOffersRequirement] =
    useState(null);

  const quantityOptions = [
    { value: "", label: "Select Range" },
    { value: "50-100", label: "50-100 MT" },
    { value: "100-200", label: "100-200 MT" },
    { value: "200-500", label: "200-500 MT" },
    { value: "Above-500", label: "Above 500 MT" },
  ];

  const offerStatusOptions = [
    { value: "", label: "Select Offer Status" },
    { value: "Indicative", label: "Indicative" },
    { value: "Firm Offer", label: "Firm Offer" },
  ];

  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    nextAction: null,
    autoClose: null,
  });

  const handleModalClose = () => {
    const action = modal.nextAction;
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));
    if (action) action();
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    setMatchingOffers(false);
  };

  const getBuyerInfo = useCallback(async (buyerEmail) => {
    try {
      const response = await fetchData({
        target: "listAMTPLUsers",
        userOrder: "AMTPLUserID",
        rowStart: 0,
        pageSize: 100,
        filterTerms: { EmailID: buyerEmail },
      });
      setBuyerInfo(response.items || []);
    } catch (error) {
      console.error("Error loading buyer info:", error);
      setBuyerInfo([]);
    }
  }, []);

  const loadAllProducts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchData({
        target: "listProducts",
        userOrder: "ProductName",
        rowStart: 0,
        pageSize: 1000,
        filterTerms: { Status: "ACTIVE" },
      });

      if (response && response.items) {
        setAllProductMaster(response.items);
        const uniqueProducts = [];
        const seenProductNames = new Set();

        response.items.forEach((item) => {
          if (!seenProductNames.has(item.ProductName)) {
            seenProductNames.add(item.ProductName);
            uniqueProducts.push({
              ProductName: item.ProductName,
              ProductID: item.ProductID,
              ProductImagePath: item.ProductImagePath,
            });
          }
        });

        setProductList(uniqueProducts);
      } else {
        console.error(
          "Error loading products: Invalid response format",
          response
        );
        setProductList([]);
        setAllProductMaster([]);
      }
    } catch (error) {
      console.error("Error loading products: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const loadProductGrades = useCallback(
    (productName) => {
      if (!productName || !allProductMaster.length) {
        setProductGradeList([]);
        return;
      }

      const productGrades = allProductMaster
        .filter((item) => item.ProductName === productName)
        .map((item) => ({
          Grade: item.Grade,
          ProductID: item.ProductID,
        }))
        .filter(
          (grade, index, self) =>
            index === self.findIndex((g) => g.Grade === grade.Grade)
        );

      setProductGradeList(productGrades);
    },
    [allProductMaster]
  );

  const searchRequirements = useCallback(
    async (searchValue, newRowStart = rowStart) => {
      setLoading(true);
      try {
        const filterTerms = {
          CategoryType: "requirement",
          GPEmailID: activeUser,
          CreatedByEmailIDNot: activeUser,
        };

        const today = new Date().toISOString().split("T")[0];
        if (mode === "Close") {
          filterTerms.ValidUntilLT = `'${today}'`;
        } else {
          filterTerms.ValidUntilGTE = `'${today}'`;
        }

        if (searchValue.requirementNumber) {
          filterTerms.requirementNumber = searchValue.requirementNumber;
        }

        if (searchValue.searchProduct && searchValue.searchProductGrade) {
          const product = allProductMaster.find(
            (p) =>
              p.ProductName === searchValue.searchProduct &&
              p.Grade === searchValue.searchProductGrade
          );
          if (product) {
            filterTerms.ProductID = product.ProductID;
          } else {
            console.warn(
              `No product found for ProductName: ${searchValue.searchProduct}, Grade: ${searchValue.searchProductGrade}`
            );
            setRequirementList([]);
            setTotalItems(0);
            setLoading(false);
            return;
          }
        } else if (searchValue.searchProduct) {
          const products = allProductMaster.filter(
            (p) => p.ProductName === searchValue.searchProduct
          );
          if (products.length > 0) {
            filterTerms.ProductID = products.map((p) => p.ProductID);
          }
        }

        // Handle quantity range filtering
        if (searchValue.searchQuantityRange) {
          const [min, max] = searchValue.searchQuantityRange.split("-");
          if (min === "Above") {
            filterTerms.QuantityGT = max;
          } else {
            filterTerms.QuantityGT = min;
            filterTerms.QuantityLT = max;
          }
          delete filterTerms.searchQuantityRange;
        }


        if (searchValue.offerStatus) {
          filterTerms.OfferStatus = searchValue.offerStatus;
        }

        const body = {
          target: "listRequirements",
          userOrder: "RequirementsID",
          rowStart: newRowStart,
          pageSize: pageSize,
          filterTerms,
        };

        console.log("Request body:", JSON.stringify(body, null, 2)); // Debug API request

        const response = await fetchData(body);

        const items = Array.isArray(response.items) ? response.items : response;
        const modifiedItems = items.map((item) => ({
          ...item,
          id: item.RequirementsID,
        }));

        setRequirementList(modifiedItems);
        setTotalItems(response.total || items.length || 0);
      } catch (error) {
        console.error("Error loading requirements:", error);
        setRequirementList([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [pageSize, rowStart, setLoading, activeUser, mode, allProductMaster]
  );

  const handleSearch = (searchValues) => {
    setRowStart(0);
    setSearchValues(searchValues);
    searchRequirements(searchValues, 0);
    setMatchingOffers(false);
  };

  const handleClearFilters = () => {
    const defaultValues = {
      requirementNumber: "",
      searchProduct: "",
      searchProductGrade: "",
      searchQuantityRange: "",
      offerStatus: "",
    };
    setSearchValues(defaultValues);
    setRowStart(0);
    searchRequirements(defaultValues, 0);
  };

  const handlePageChange = (page) => {
    const newRowStart = (page - 1) * pageSize;
    setRowStart(newRowStart);
    searchRequirements(searchValues, newRowStart);
  };

  const handleAdd = () => {
    setIsFormVisible((prev) => !prev);
    setFormMode("add");
    setSelectedRequirement(null);
    setMatchingOffers(false);
  };

  const handleActionClick = (event, row, mode) => {
    event.stopPropagation();
    setSelectedRequirement(row);
    setFormMode(mode);
    setIsFormVisible(true);
    setMatchingOffers(false);
  };

  const handleRowClick = (row, event) => {
    setMatchingOffers(true);
    setMatchingOffersRequirement(row);
    setIsFormVisible(false);
    setSelectedOffer(row)
  };

  const currentPage = Math.floor(rowStart / pageSize) + 1;

  const fields = [
    {
      name: "requirementNumber",
      label: "Requirement Number",
      type: "text",
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, requirementNumber: value })),
      value: searchValues.requirementNumber,
    },
    {
      name: "searchProduct",
      label: "Product",
      type: "dropdown",
      options: productList.map((item) => ({
        value: item.ProductName,
        label: item.ProductName,
      })),
      value: searchValues.searchProduct,
      onChange: (value) => {
        setSearchValues((prev) => ({
          ...prev,
          searchProduct: value,
          searchProductGrade: "",
        }));
        loadProductGrades(value);
      },
    },
    {
      name: "searchProductGrade",
      label: "Product Grade",
      type: "dropdown",
      options: productGradeList.map((item) => ({
        value: item.Grade,
        label: item.Grade,
      })),
      value: searchValues.searchProductGrade,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchProductGrade: value }));
      },
    },
    {
      name: "searchQuantityRange",
      label: "Quantity Range (MT)",
      type: "dropdown",
      options: quantityOptions,
      value: searchValues.searchQuantityRange,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchQuantityRange: value }));
      },
    },
    // {
    //   name: "offerStatus",
    //   label: "Offer Status",
    //   type: "dropdown",
    //   options: offerStatusOptions,
    //   value: searchValues.offerStatus,
    //   onChange: (value) => {
    //     setSearchValues((prev) => ({ ...prev, offerStatus: value }));
    //   },
    // },
  ];

  const columns = [
    { id: "RequirementNumber", numeric: false, label: "Req No." },
    // { id: "BuyerName", numeric: false, label: "Buyer Name" },
    { id: "BuyerEmailID", numeric: false, label: "Buyer Email ID" },
    {
      id: "Product",
      numeric: false,
      label: "Product",
      renderCell: (row) => {
        const pn = allProductMaster.find(
          (item) => item.ProductID === row.ProductID
        );
        return pn ? pn.ProductName : "";
      },
    },
    {
      id: "Grade",
      numeric: false,
      label: "Grade",
      renderCell: (row) => {
        const pn = allProductMaster.find(
          (item) => item.ProductID === row.ProductID
        );
        return pn ? pn.Grade : "";
      },
    },
    { id: "Quantity", numeric: true, label: "Quantity(MT)" },
    { id: "OriginCountry", numeric: false, label: "Origin" },
    {
      id: "DeliveryLocation",
      numeric: false,
      label: "Destination",
      renderCell: (row) => {
        let res = row.DeliveryLocation.split("~");
        if (Array.isArray(res)) {
          return res[0];
        }
        return "";
      },
    },
    {
      id: "ShipmentPeriod",
      numeric: false,
      label: "Shipment Period",
      renderCell: (row) => {
        let dpFrom = row.DeliveryPeriodFrom
          ? dayjs(row.DeliveryPeriodFrom).format("DD-MM-YYYY")
          : "";
        let dpTill = row.DeliveryPeriodTill
          ? dayjs(row.DeliveryPeriodTill).format("DD-MM-YYYY")
          : "";
        return dpFrom !== "" && dpTill !== "" ? dpFrom + " to " + dpTill : "";
      },
    },
    {
      id: "DeliveryPort",
      numeric: false,
      label: "Delivery Port",
      renderCell: (row) => {
        let res = row.DeliveryLocation.split("~");
        if (Array.isArray(res) && res.length > 0) {
          return res[1];
        }
        return "";
      },
    },

    { id: "Incoterm", numeric: false, label: "Incoterms" },
    {
      id: "ProductPackaging",
      numeric: false,
      label: "Packaging",
      renderCell: (row) => {
        const jsonLike = row.ProductPackaging.replace(/{|}/g, "")
          .split(",")
          .map((part) => part.split("="))
          .reduce((obj, [key, value]) => {
            obj[key.trim()] = value ? value.trim() : "";
            return obj;
          }, {});
        return jsonLike.item;
      },
    },
    {
      id: "RequirementDate",
      numeric: false,
      label: "Created",
      renderCell: (row) =>
        row.RequirementDate
          ? dayjs(row.RequirementDate).format("DD-MM-YYYY")
          : "",
    },
    {
      id: "ValidUntil",
      numeric: false,
      label: "Expires",
      renderCell: (row) =>
        row.ValidUntil ? dayjs(row.ValidUntil).format("DD-MM-YYYY") : "",
    },
  ];

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);
        const groups = decodedToken["cognito:groups"] || [];
        if (!groups.includes("AMTPL_GLOBAL_PARTNERS")) {
          await Auth.signOut();
          navigate("/login/global-partner");
        } else {
          setActiveUser(user.attributes.email);
          getBuyerInfo(user.attributes.email);
        }
      } catch (error) {
        console.error("Error checking user:", error);
        await Auth.signOut();
        navigate("/login/global-partner");
      }
    };
    checkUser();
    loadAllProducts();
  }, [navigate, loadAllProducts, getBuyerInfo]);

  useEffect(() => {
    if (activeUser && allProductMaster.length > 0) {
      searchRequirements(searchValues);
    }
  }, [
    rowStart,
    pageSize,
    activeUser,
    mode,
    searchRequirements,
    searchValues,
    allProductMaster,
  ]);

  const selectedRows = matchingOffersRequirement
    ? [matchingOffersRequirement.RequirementsID || matchingOffersRequirement.id]
    : [];
 const IOSSwitch = styled((props) => (
    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
  ))(({ theme }) => ({
    width: 42,
    height: 26,
    padding: 0,
    display: 'flex',
    '& .MuiSwitch-switchBase': {
      padding: 0,
      margin: 2,
      transition: 'transform 300ms ease-in-out',
      '&.Mui-checked': {
        transform: 'translateX(16px) !important',
        color: '#fff',
        '& + .MuiSwitch-track': {
          backgroundColor: '#de6b14',
          opacity: 1,
          border: 0,
        },
      },
      '&.Mui-focusVisible .MuiSwitch-thumb': {
        color: '#de6b14',
        border: '6px solid #fff',
      },
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: 22,
      height: 22,
      transition: 'all 300ms ease-in-out !important',
    },
    '& .MuiSwitch-track': {
      borderRadius: 13,
      backgroundColor: '#ccc',
      opacity: 1,
      transition: 'background-color 300ms ease-in-out !important',
    },
  }));
  return (
    <div>
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        buttonText="OK"
        autoClose={modal.autoClose}
      />
      {/* <div className="table-header-re">
        <div className="head-btn">
          {mode}
          <Switch
            checked={mode === "Open"}
            onChange={(e) =>
              handleModeChange(e.target.checked ? "Open" : "Close")
            }
            sx={{
              marginRight: "20px",
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: "#de6b14",
                "&:hover": {
                  backgroundColor: "rgba(222, 107, 20, 0.04)",
                },
              },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "#de6b14",
              },
              "& .MuiSwitch-track": {
                backgroundColor: "#ccc",
              },
            }}
          />
        </div>
      </div> */}
      <div className="table-header-re">
        <div className="head-btn">
          {mode}
          <FormControlLabel
            control={
              <IOSSwitch
                checked={mode === "Open"}
                onChange={(e) => handleModeChange(e.target.checked ? "Open" : "Close")}
                name="mode"
                sx={{ m: 1 }}
              />
            }
            label=""
            sx={{ marginRight: 1 }}
          />
          {/* <button className="submit-btn" onClick={handleAdd}>
            {isFormVisible ? "Cancel" : "Add"}
          </button> */}
        </div>
      </div>
      <div className="search-filter4">
        <div className="title4">
          <div className="head-text">My Requirements</div>
        </div>
        <div className="filter4">
          <SearchFilter fields={fields} onSearch={handleSearch} />
          {/* <Button variant="outlined" onClick={handleClearFilters}>
                    Clear Filters
                </Button> */}
        </div>
      </div>
      {requirementList.length === 0 && (
        <div>No requirements found for the selected filters.</div>
      )}
      <DataTable
        columns={columns}
        data={requirementList}
        total={totalItems}
        title="Requirement Information"
        rowsPerPage={pageSize}
        onPageChange={handlePageChange}
        currentPage={currentPage}
        onRowClick={handleRowClick}
        selectedRows={selectedRows}
      />
      {selectedOffer && matchingOffers && selectedOffer.RequirementsID && (
        <RequirementDetails selectedOffer={selectedOffer} allProductMaster={allProductMaster}/>
        
      )}
      {isFormVisible && (
        <RequirementForm
          formMode={formMode}
          setFormMode={setFormMode}
          selectedRequirement={selectedRequirement}
          setIsFormVisible={setIsFormVisible}
          productList={productList}
          allProductMaster={allProductMaster}
          activeUser={activeUser}
          buyerInfo={buyerInfo}
          totalItems={totalItems}
          setModal={setModal}
          searchRequirements={searchRequirements}
          searchValues={searchValues}
          navigate={navigate}
        />
      )}
      {matchingOffers && matchingOffersRequirement && (
        <MatchingOffers
          requirementDetails={matchingOffersRequirement}
          activeUser={activeUser}
        />
      )}

    </div>
  );
};

export default Requirements;